import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Visibility,
  Download,
  Receipt,
  QrCode,
  Search,
  FilterList,
  CalendarToday,
  Group,
  Euro,
  MoreVert,
  Payment,
  Cancel,
  CheckCircle,
  Edit as EditIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { API_BASE_URL, authFetch } from '../../config/api.config';
import SelfServiceBookingDialog from '../customer/SelfServiceBookingDialog';
import BookingSystemAPI, { SelfServiceResult } from '../../services/BookingSystemAPI';

interface Booking {
  id: number;
  bookingNumber: string;
  scheduleDate: string;
  startTime: string;
  variant: 'Standard' | 'Premium';
  tourTypeCode?: string; // Code of the tour type (e.g., "KAFFEE-KUCHEN-FLOSSFAHRT")
  tourTypeName?: string; // Display name of the tour type (e.g., "Premium Floßfahrt Montag-Donnerstag")
  participantCount: number;
  // Participant categories (Phase: Teilnehmer-Kategorien)
  adultCount?: number;
  childCount?: number;
  infantCount?: number;
  freeParticipantWithSeatCount?: number;
  freeParticipantNoSeatCount?: number;
  totalAmount: number;
  basePrice?: number;
  voucherDiscount?: number; // Amount redeemed from voucher
  finalAmount?: number; // Final amount after voucher discount
  status: 'Pending' | 'Confirmed' | 'Paid' | 'Cancelled' | 'Completed';
  paymentStatus: 'Pending' | 'PartiallyPaid' | 'Paid' | 'Refunded';
  createdAt: string;
  createdAtTime?: string; // Time when booking was created (HH:mm:ss)
  hasTicket: boolean;
  hasInvoice: boolean;
  // T-088 (v1.8.20): Altfall-Übernahme (Anzahlung im Altverfahren) und Zahlstand
  isLegacyImport?: boolean;
  legacyDepositAmount?: number | null;
  legacyDepositPaidAt?: string | null; // yyyy-MM-dd
  legacyInvoiceNumber?: string | null;
  amountPaid?: number;
  amountDue?: number;
  addOns?: Array<{
    id: number;
    name: string;
    quantity: number;
    unitPrice: number;
  }>;
}

type Order = 'asc' | 'desc';
type OrderBy = 'bookingNumber' | 'scheduleDate' | 'tourTypeCode' | 'participantCount' | 'finalAmount' | 'status' | 'paymentStatus' | 'createdAt';

const BookingHistory: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [totalBookings, setTotalBookings] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('createdAt');

  useEffect(() => {
    loadBookings();
  }, [page, rowsPerPage, statusFilter, order, orderBy]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken'); // Changed from 'accessToken' to 'authToken'

      // Skip loading if no token (user not authenticated)
      if (!token) {
        console.log('No auth token, skipping booking load');
        setBookings([]);
        setTotalBookings(0);
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: (page + 1).toString(),
        pageSize: rowsPerPage.toString(),
        sortBy: orderBy,
        sortOrder: order,
        ...(statusFilter !== 'all' && { status: statusFilter })
      });

      // T-009: authFetch setzt die Auth-Header und refresht bei 401 automatisch
      const response = await authFetch(`/api/user/bookings?${params}`, {});

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Buchungen');
      }

      const data = await response.json();
      setBookings(data.items || []);
      setTotalBookings(data.totalCount || 0);
    } catch (err) {
      console.error('Error loading bookings:', err);
      setError('Buchungen konnten nicht geladen werden');
      // v1.8.35: keine Beispiel-Buchungen mehr anzeigen (früher Mock-Daten bei Ladefehlern, für Gäste irreführend)
      setBookings([]);
      setTotalBookings(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Confirmed': return 'success';
      case 'Pending': return 'warning';
      case 'Paid': return 'success';
      case 'Cancelled': return 'error';
      case 'Completed': return 'info';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'Paid': return 'success';
      case 'PartiallyPaid': return 'warning';
      case 'Pending': return 'error';
      case 'Refunded': return 'default';
      default: return 'default';
    }
  };

  // T-088 (v1.8.20): Zahlstatus-Beschriftung; Pending mit Teilzahlung (z. B. Alt-Anzahlung) = "Anzahlung geleistet"
  const formatEuro = (value: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
  const formatDateDe = (isoDate: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : isoDate;
  };
  const hasLegacyDeposit = (booking: Booking) => (booking.legacyDepositAmount ?? 0) > 0;
  const getPaymentLabel = (booking: Booking): string => {
    if (booking.paymentStatus === 'Paid') return 'Bezahlt';
    if (booking.paymentStatus === 'PartiallyPaid') return 'Teilzahlung';
    if (booking.paymentStatus === 'Pending') {
      return (booking.amountPaid ?? 0) > 0 ? 'Anzahlung geleistet' : 'Ausstehend';
    }
    return 'Erstattet';
  };
  const getPaymentColor = (booking: Booking): string => {
    if (booking.paymentStatus === 'Pending' && (booking.amountPaid ?? 0) > 0) return 'warning';
    return getPaymentStatusColor(booking.paymentStatus);
  };

  // German status labels
  const getStatusLabel = (status: string): string => {
    const statusLabels: Record<string, string> = {
      'Pending': 'Ausstehend',
      'Draft': 'Entwurf',
      'Option': 'Option',
      'OfferSent': 'Angebot gesendet',
      'OfferAccepted': 'Angebot angenommen',
      'OfferDeclined': 'Angebot abgelehnt',
      'OfferExpired': 'Angebot abgelaufen',
      'Confirmed': 'Bestätigt',
      'Paid': 'Bezahlt',
      'Completed': 'Abgeschlossen',
      'Cancelled': 'Storniert',
      'NoShow': 'Nicht erschienen',
      'Refunded': 'Erstattet'
    };
    return statusLabels[status] || status;
  };

  // Actions menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  // Self-Service Dialog state
  const [selfServiceDialogOpen, setSelfServiceDialogOpen] = useState(false);
  const [selfServiceBooking, setSelfServiceBooking] = useState<Booking | null>(null);
  const [selfServiceUpdated, setSelfServiceUpdated] = useState(false);
  const [selfServiceKey, setSelfServiceKey] = useState(0);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, booking: Booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBooking(null);
  };

  // Determine available actions based on booking status
  const getAvailableActions = (booking: Booking) => {
    // Self-service is allowed for confirmed bookings that are not cancelled/completed
    const canSelfService = ['Confirmed', 'Paid'].includes(booking.status) &&
                          !['Cancelled', 'Completed'].includes(booking.status);

    const actions = {
      canView: true,
      canPay: ['Confirmed', 'OfferAccepted'].includes(booking.status) && booking.paymentStatus !== 'Paid',
      canDownloadInvoice: booking.hasInvoice,
      // T-096 (v1.8.35): das Backend entscheidet (ab dem 5. Tag vor der Fahrt bei vollständiger Zahlung)
      canDownloadTicket: booking.hasTicket,
      canCancel: ['Pending', 'Confirmed', 'Option', 'OfferAccepted'].includes(booking.status),
      canSelfService: canSelfService // New: Allow self-service updates
    };
    return actions;
  };

  // Handler for opening Self-Service dialog
  const handleOpenSelfService = (booking: Booking) => {
    setSelfServiceBooking(booking);
    setSelfServiceDialogOpen(true);
    setSelfServiceKey(prev => prev + 1);
    handleMenuClose();
  };

  // Handler for Self-Service success
  const handleSelfServiceSuccess = (result: SelfServiceResult) => {
    setSelfServiceUpdated(true);
  };

  // Handler for closing Self-Service dialog
  const handleSelfServiceClose = async () => {
    setSelfServiceDialogOpen(false);
    setSelfServiceBooking(null);
    if (selfServiceUpdated) {
      setSelfServiceUpdated(false);
      await loadBookings();
    }
  };

  const handleViewDetails = (bookingId: number) => {
    // Navigate to booking confirmation/details page
    window.location.href = `/booking-confirmation/${bookingId}`;
  };

  const handleDownloadInvoice = async (bookingId: number) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/user/bookings/${bookingId}/invoice`, {});

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung-${bookingId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const handleDownloadTicket = async (bookingId: number) => {
    // T-096 (v1.8.35): Bordticket als PDF, Fehlermeldung des Backends anzeigen (z. B. noch nicht vollständig bezahlt)
    const booking = bookings.find(b => b.id === bookingId);
    try {
      const response = await authFetch(`${API_BASE_URL}/user/bookings/${bookingId}/ticket`, {});

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Bordticket-${booking?.bookingNumber || bookingId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        let message = 'Das Bordticket konnte nicht heruntergeladen werden.';
        try {
          const data = await response.json();
          if (data?.message) message = data.message;
        } catch {
          // keine JSON-Antwort
        }
        setError(message);
      }
    } catch (error) {
      console.error('Error downloading ticket:', error);
      setError('Das Bordticket konnte nicht heruntergeladen werden.');
    }
  };

  // Sorting handler - resets page to 0 for server-side sorting across all pages
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting changes
  };

  // Client-side filtering only (sorting is done server-side)
  const filteredBookings = bookings
    .filter(booking =>
      booking.bookingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.scheduleDate.includes(searchTerm)
    );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Meine Buchungen
      </Typography>
      
      {/* Filters */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          placeholder="Suche nach Buchungsnummer oder Datum..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="Confirmed">Bestätigt</MenuItem>
            <MenuItem value="Pending">Ausstehend</MenuItem>
            <MenuItem value="Completed">Abgeschlossen</MenuItem>
            <MenuItem value="Cancelled">Storniert</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filteredBookings.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Keine Buchungen gefunden
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Sie haben noch keine Floßfahrten gebucht.
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} href="/booking">
            Jetzt buchen
          </Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'bookingNumber'}
                      direction={orderBy === 'bookingNumber' ? order : 'asc'}
                      onClick={() => handleRequestSort('bookingNumber')}
                    >
                      Buchungsnr.
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'scheduleDate'}
                      direction={orderBy === 'scheduleDate' ? order : 'asc'}
                      onClick={() => handleRequestSort('scheduleDate')}
                    >
                      Datum & Zeit
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'tourTypeCode'}
                      direction={orderBy === 'tourTypeCode' ? order : 'asc'}
                      onClick={() => handleRequestSort('tourTypeCode')}
                    >
                      Variante
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === 'participantCount'}
                      direction={orderBy === 'participantCount' ? order : 'asc'}
                      onClick={() => handleRequestSort('participantCount')}
                    >
                      Personen
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'finalAmount'}
                      direction={orderBy === 'finalAmount' ? order : 'asc'}
                      onClick={() => handleRequestSort('finalAmount')}
                    >
                      Betrag
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleRequestSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === 'paymentStatus'}
                      direction={orderBy === 'paymentStatus' ? order : 'asc'}
                      onClick={() => handleRequestSort('paymentStatus')}
                    >
                      Zahlung
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBookings.map((booking) => (
                  <TableRow key={booking.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {booking.bookingNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(booking.createdAt), 'dd.MM.yyyy', { locale: de })} {booking.createdAtTime || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarToday fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2">
                            {format(new Date(booking.scheduleDate), 'dd. MMMM yyyy', { locale: de })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.startTime} Uhr
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                          {booking.tourTypeCode || booking.variant}
                        </Typography>
                        {booking.tourTypeName && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {booking.tourTypeName}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        <Group fontSize="small" color="action" />
                        <Typography variant="body2">{booking.participantCount}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(booking.finalAmount ?? booking.totalAmount)}
                        </Typography>
                        {(booking.voucherDiscount ?? 0) > 0 && (
                          <Typography variant="caption" color="success.main" display="block">
                            (-{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(booking.voucherDiscount!)} Gutschein)
                          </Typography>
                        )}
                        {/* T-088 (v1.8.20): Alt-Anzahlung aus der Altfall-Übernahme und Restbetrag */}
                        {hasLegacyDeposit(booking) && (
                          <Typography variant="caption" color="success.main" display="block">
                            Anzahlung {formatEuro(booking.legacyDepositAmount!)}
                            {booking.legacyDepositPaidAt ? ` bezahlt am ${formatDateDe(booking.legacyDepositPaidAt)}` : ' bezahlt'}
                            {' (Altverfahren)'}
                          </Typography>
                        )}
                        {(booking.amountPaid ?? 0) > 0 && booking.paymentStatus !== 'Paid' && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Restbetrag {formatEuro(booking.amountDue ?? Math.max(0, (booking.finalAmount ?? booking.totalAmount) - (booking.amountPaid ?? 0)))}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getStatusLabel(booking.status)}
                        size="small"
                        color={getStatusColor(booking.status) as any}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getPaymentLabel(booking)}
                        size="small"
                        color={getPaymentColor(booking) as any}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, booking)}
                        sx={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Actions Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                minWidth: 200,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                borderRadius: '8px'
              }
            }}
          >
            {selectedBooking && (() => {
              const actions = getAvailableActions(selectedBooking);
              return (
                <>
                  <MenuItem onClick={() => { handleViewDetails(selectedBooking.id); handleMenuClose(); }}>
                    <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
                    <ListItemText>Details anzeigen</ListItemText>
                  </MenuItem>

                  {actions.canSelfService && (
                    <MenuItem onClick={() => handleOpenSelfService(selectedBooking)}>
                      <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#1976d2' }} /></ListItemIcon>
                      <ListItemText>Buchung anpassen</ListItemText>
                    </MenuItem>
                  )}

                  {actions.canPay && (
                    <MenuItem onClick={() => { window.location.href = `/checkout/${selectedBooking.id}`; handleMenuClose(); }}>
                      <ListItemIcon><Payment fontSize="small" sx={{ color: '#7B3F00' }} /></ListItemIcon>
                      <ListItemText>Jetzt bezahlen</ListItemText>
                    </MenuItem>
                  )}

                  {actions.canDownloadInvoice && (
                    <MenuItem onClick={() => { handleDownloadInvoice(selectedBooking.id); handleMenuClose(); }}>
                      <ListItemIcon><Receipt fontSize="small" /></ListItemIcon>
                      <ListItemText>Rechnung herunterladen</ListItemText>
                    </MenuItem>
                  )}

                  {actions.canDownloadTicket && (
                    <MenuItem onClick={() => { handleDownloadTicket(selectedBooking.id); handleMenuClose(); }}>
                      <ListItemIcon><QrCode fontSize="small" /></ListItemIcon>
                      <ListItemText>Bordticket herunterladen</ListItemText>
                    </MenuItem>
                  )}

                  {actions.canCancel && (
                    <>
                      <Divider />
                      <MenuItem
                        onClick={() => { alert('Stornierung: Bitte kontaktieren Sie uns unter +49 170 7744661'); handleMenuClose(); }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon><Cancel fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                        <ListItemText>Stornieren</ListItemText>
                      </MenuItem>
                    </>
                  )}
                </>
              );
            })()}
          </Menu>

          <TablePagination
            component="div"
            count={totalBookings}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Einträge pro Seite:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
          />
        </>
      )}

      {/* Self-Service Booking Dialog */}
      {selfServiceBooking && (
        <SelfServiceBookingDialog
          key={selfServiceKey}
          booking={{
            id: selfServiceBooking.id,
            bookingNumber: selfServiceBooking.bookingNumber,
            tripDate: selfServiceBooking.scheduleDate,
            tripTime: selfServiceBooking.startTime,
            tourTypeName: selfServiceBooking.tourTypeName || selfServiceBooking.variant,
            participantCount: selfServiceBooking.participantCount,
            participants: {
              adultCount: selfServiceBooking.adultCount ?? selfServiceBooking.participantCount,
              childCount: selfServiceBooking.childCount ?? 0,
              infantCount: selfServiceBooking.infantCount ?? 0,
              freeParticipantWithSeatCount: selfServiceBooking.freeParticipantWithSeatCount ?? 0,
              freeParticipantNoSeatCount: selfServiceBooking.freeParticipantNoSeatCount ?? 0
            },
            addOns: selfServiceBooking.addOns || [],
            totalPrice: selfServiceBooking.finalAmount || selfServiceBooking.totalAmount,
            basePrice: selfServiceBooking.basePrice || 67.50, // Default Staffelpreis
            voucherDiscount: selfServiceBooking.voucherDiscount || 0
          }}
          isOpen={selfServiceDialogOpen}
          onClose={handleSelfServiceClose}
          onSuccess={handleSelfServiceSuccess}
        />
      )}
    </Box>
  );
};

export default BookingHistory;
